import { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  errorMessage,
  getAppSettings,
  getPublicMatches,
  getStreamUrls,
  regenerateAdminThumbnail,
} from "@/lib/api";
import { coerceBool } from "@/lib/coerce";
import { getSideLogos } from "@/lib/matchLogos";
import { unscopedKey } from "@/lib/queryClient";
import { useToast } from "@/components/ui/toast";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import {
  getPlaybackSpeed,
  getResumePosition,
  savePlaybackPosition,
} from "@/lib/playbackMemory";
import { HlsVideo } from "@/features/playback/HlsVideo";
import { useAirPlay } from "@/features/playback/useAirPlay";
import { useChromecast } from "@/features/playback/useChromecast";
import { useVodHeartbeat } from "@/features/playback/useVodHeartbeat";
import { useAuthStore } from "@/stores/auth";
import { LOCKDOWN_MODE } from "@/features/navigation/lockdown";
import { useScoreRevealStore, useScoreRevealed } from "@/stores/scoreReveal";
import type { AppSettings, Match } from "@/lib/types";
import {
  firstReadySlot,
  hasRecordedScore,
  sortMatchesByDateDesc,
} from "@/features/season/seasonHelpers";
import { usePublicVideoShortcuts } from "../hooks/usePublicVideoShortcuts";

/**
 * M11a section B — public game view at `/match/$slug` and
 * `/match/$slug/$slot`.
 *
 * Three responsibilities:
 *   1. Resolve the slug → match by reading the same public
 *      `/api/matches` list the season view uses (cached). If the
 *      slug isn't found, render a "not found" shell with a back
 *      link instead of throwing.
 *   2. Mount the HLS player for the active slot. Active slot
 *      defaults to the match's first ready slot when the URL
 *      omits one; explicit `/$slot` URLs win.
 *   3. Wire the side concerns: position memory, heartbeat,
 *      keyboard shortcuts, AirPlay/Chromecast buttons, score-reveal, OG
 *      meta tags.
 *
 * The legacy `js/player.js` was ~700 LOC; this React port is
 * smaller because M5 already shipped `<HlsVideo>` + the heartbeat
 * hook (which handles kill-propagation toast + auto-pause). The
 * public game view reuses both with the no-token URL shape.
 */
/** Legacy URL ↔ backend slot transform.
 *
 * The backend's `match.video_status` keys are underscored
 * (`first_half`, `second_half`, `full`). The legacy public URLs
 * used hyphenated forms in the path (`/match/foo/second-half`)
 * for readability. To keep old shareable links working AND emit
 * the same canonical URL shape, normalize both directions:
 *
 *   URL "second-half"  →  slot key "second_half"  (read)
 *   slot key "second_half"  →  URL "second-half"  (write)
 *
 * Reviewer-flagged on PR #21 P1.
 */
export function urlSlotToKey(urlSlot: string | undefined): string | undefined {
  if (!urlSlot) return undefined;
  return urlSlot.replace(/-/g, "_");
}
export function slotKeyToUrl(slotKey: string): string {
  return slotKey.replace(/_/g, "-");
}

/** "Back to Matches" link for the public game view.
 *
 * When the viewer arrived from a team page, `fromTeamSlug` is the
 * `?from=` breadcrumb — the link returns to `/teams/$teamSlug` so the
 * back motion is coherent. Absent (season view / direct link) ⇒ the
 * link falls back to `/`, the legacy behavior. */
function BackToMatchesLink({
  fromTeamSlug,
  label = "← Back to matches",
  className,
}: {
  fromTeamSlug: string | undefined;
  label?: React.ReactNode;
  className?: string;
}) {
  if (fromTeamSlug) {
    return (
      <Link
        to="/teams/$teamSlug"
        params={{ teamSlug: fromTeamSlug }}
        data-testid="game-back-link"
        className={className}
      >
        {label}
      </Link>
    );
  }
  return (
    <Link to="/" data-testid="game-back-link" className={className}>
      {label}
    </Link>
  );
}

export function GameShell() {
  const params = useParams({ strict: false });
  const slug = (params as { slug?: string }).slug ?? "";
  const slotParam = urlSlotToKey((params as { slot?: string }).slot);
  const navigate = useNavigate();
  // `?from=<teamSlug>` — set when the viewer arrived from a team page
  // (`/teams/$teamSlug`). Drives the "Back to Matches" link target so
  // it returns to that team's match list instead of the marketing
  // landing. `strict: false` because the param-shape is shared across
  // both `/match/$slug` and `/match/$slug/$slot`.
  const search = useSearch({ strict: false });
  const fromTeamSlug = (search as { from?: string }).from;

  const matchesQuery = useQuery({
    queryKey: unscopedKey("public-matches"),
    queryFn: getPublicMatches,
    staleTime: 60_000,
  });
  const settingsQuery = useQuery({
    queryKey: unscopedKey("app-settings"),
    queryFn: getAppSettings,
    staleTime: 5 * 60_000,
  });
  const sortedMatches = useMemo(
    () => sortMatchesByDateDesc(matchesQuery.data ?? []),
    [matchesQuery.data],
  );
  const match = sortedMatches.find((m) => m.slug === slug);

  // Resolve the active slot:
  //   1. URL-provided slot, normalized from hyphenated URL form to
  //      the backend key, AND `status === "ready"`. Non-ready slots
  //      (transcoding / error / none) MUST fall through to the
  //      ready-slot fallback so we don't mount <HlsVideo> against
  //      a non-playable stream and run a heartbeat that produces
  //      no viewer-visible video. Legacy `playSlot` only fires on
  //      ready slots; reviewer-flagged on PR #21 re-review P2.
  //   2. First ready slot for the match
  //   3. null → render the "no playable video yet" placeholder
  const activeSlot = useMemo(() => {
    if (!match) return null;
    if (slotParam && match.video_status?.[slotParam] === "ready")
      return slotParam;
    return firstReadySlot(match);
  }, [match, slotParam]);

  if (matchesQuery.isLoading) {
    return (
      <main
        data-testid="game-loading"
        className="min-h-screen bg-canvas text-ink-muted flex items-center justify-center"
      >
        Loading match…
      </main>
    );
  }
  if (matchesQuery.isError) {
    return (
      <main
        data-testid="game-error"
        className="min-h-screen bg-canvas text-destructive flex flex-col items-center justify-center gap-3 p-6"
      >
        <p className="text-sm font-mono">
          Couldn&rsquo;t load match. Please refresh in a moment.
        </p>
        <BackToMatchesLink
          fromTeamSlug={fromTeamSlug}
          className="text-xs text-accent hover:underline font-display uppercase tracking-wider"
        />
      </main>
    );
  }
  if (!match) {
    return (
      <main
        data-testid="game-not-found"
        className="min-h-screen bg-canvas text-ink-muted flex flex-col items-center justify-center gap-3 p-6"
      >
        <h1 className="text-2xl font-display">Match not found</h1>
        <p className="text-sm font-mono">
          The match URL &ldquo;{slug}&rdquo; doesn&rsquo;t resolve.
        </p>
        <BackToMatchesLink
          fromTeamSlug={fromTeamSlug}
          className="text-xs text-accent hover:underline font-display uppercase tracking-wider"
        />
      </main>
    );
  }

  return (
    <GameBody
      match={match}
      activeSlot={activeSlot}
      fromTeamSlug={fromTeamSlug}
      sortedMatches={sortedMatches}
      settings={settingsQuery.data?.settings}
      clubLogoUrl={settingsQuery.data?.assets?.club_logo_url ?? null}
      onSelectSlot={(nextSlot) => {
        void navigate({
          to: "/match/$slug/$slot",
          // Emit the hyphenated URL form (matches the legacy
          // public match URLs); `urlSlotToKey` reverses on read.
          params: { slug: match.slug, slot: slotKeyToUrl(nextSlot) },
          // Keep the `?from=` breadcrumb so switching halves doesn't
          // strip the team-page back target.
          search: fromTeamSlug ? { from: fromTeamSlug } : {},
          replace: true,
        });
      }}
    />
  );
}

function GameBody({
  match,
  activeSlot,
  fromTeamSlug,
  sortedMatches,
  settings,
  clubLogoUrl,
  onSelectSlot,
}: {
  match: Match;
  activeSlot: string | null;
  fromTeamSlug: string | undefined;
  sortedMatches: Match[];
  settings: AppSettings | undefined;
  clubLogoUrl: string | null;
  onSelectSlot: (slot: string) => void;
}) {
  const authMe = useAuthStore((s) => s.me);
  // Lockdown presents a public-only site — suppress every signed-in
  // platform affordance (Edit / Regen Thumb / Coach this match) even
  // when an admin/coach/uploader token is present.
  const isSignedIn = !LOCKDOWN_MODE && !!authMe?.user;
  // Legacy public game-view header gates (PR #21 P2 fix):
  //   - Edit Match button → `canEdit()` (uploader OR admin)
  //   - Regen Thumb → admin-only; calls the existing admin endpoint
  //     from the public game view for operators watching the match
  //   - "Coach this match →" → coach / assistant_coach / team_admin
  //     membership OR global admin
  const roles = authMe?.user?.roles ?? [];
  const isAdmin = !LOCKDOWN_MODE && roles.includes("admin");
  const canEdit = !LOCKDOWN_MODE && (isAdmin || roles.includes("uploader"));
  const isCoachOrAdmin =
    !LOCKDOWN_MODE &&
    (isAdmin ||
      !!authMe?.memberships?.some((m) =>
        ["coach", "assistant_coach", "team_admin"].includes(m.role),
      ));

  const reveal = useScoreRevealStore((s) => s.reveal);
  const revealed = useScoreRevealed(match.id);
  const scoreHidden = hasRecordedScore(match) && !revealed;

  // Prev / next in the date-sorted list (anchor on this match).
  const myIndex = sortedMatches.findIndex((m) => m.id === match.id);
  const prevMatch = myIndex > 0 ? sortedMatches[myIndex - 1] : null;
  const nextMatch =
    myIndex >= 0 && myIndex < sortedMatches.length - 1
      ? sortedMatches[myIndex + 1]
      : null;

  // Document meta — title + OG tags for share-card previews.
  const appName = settings?.app_name || "tktic";
  const title = `${match.home_team} vs ${match.away_team} · ${appName}`;
  const description = `${match.home_team} vs ${match.away_team}${
    match.date ? ` · ${match.date}` : ""
  }${match.location ? ` · ${match.location}` : ""}`;
  const ogImage = match.has_thumbnail
    ? (typeof window !== "undefined" ? window.location.origin : "") +
      `/api/matches/${encodeURIComponent(match.id)}/thumbnail`
    : undefined;
  useDocumentMeta({
    title,
    description,
    ogImage,
    ogType: "video.other",
  });

  return (
    <main
      data-testid="game-shell"
      data-match-id={match.id}
      data-active-slot={activeSlot ?? ""}
      className="min-h-screen bg-canvas text-ink"
    >
      <GameHeader
        match={match}
        prevMatch={prevMatch}
        nextMatch={nextMatch}
        fromTeamSlug={fromTeamSlug}
        isSignedIn={isSignedIn}
        canEdit={canEdit}
        canRegenerateThumbnail={isAdmin}
        isCoachOrAdmin={isCoachOrAdmin}
        activeSlot={activeSlot}
        settings={settings}
      />

      <section className="px-6 py-6 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
        <div className="flex flex-col gap-3">
          {activeSlot ? (
            <GameVideo
              match={match}
              slot={activeSlot}
              clubLogoUrl={clubLogoUrl}
            />
          ) : (
            <NoVideoPlaceholder match={match} />
          )}

          {match.format === "two_halves" ? (
            <SegmentButtons
              match={match}
              activeSlot={activeSlot}
              onSelect={onSelectSlot}
            />
          ) : null}
        </div>

        <aside
          data-testid="game-sidebar"
          className="flex flex-col gap-3"
        >
          <MatchupCard
            match={match}
            clubLogoUrl={clubLogoUrl}
            scoreHidden={scoreHidden}
            onRevealScore={() => reveal(match.id)}
          />
          <SlotStatusList match={match} settings={settings} />
          {settings && coerceBool(settings.downloads_enabled) ? (
            <DownloadList match={match} settings={settings} />
          ) : null}
        </aside>
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Header — nav + signed-in extras
// ---------------------------------------------------------------------------

function GameHeader({
  match,
  prevMatch,
  nextMatch,
  fromTeamSlug,
  isSignedIn: _isSignedIn,
  canEdit,
  canRegenerateThumbnail,
  isCoachOrAdmin,
  activeSlot,
  settings,
}: {
  match: Match;
  prevMatch: Match | null;
  nextMatch: Match | null;
  fromTeamSlug: string | undefined;
  isSignedIn: boolean;
  canEdit: boolean;
  canRegenerateThumbnail: boolean;
  isCoachOrAdmin: boolean;
  activeSlot: string | null;
  settings: AppSettings | undefined;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [thumbnailStatus, setThumbnailStatus] = useState<"success" | "error" | null>(null);
  const [thumbnailMessage, setThumbnailMessage] = useState<string | null>(null);
  const backLabel = settings?.game_back_label || "Back to Matches";
  const thumbnailMutation = useMutation({
    mutationFn: () => regenerateAdminThumbnail(match.id),
    onMutate: () => {
      setThumbnailStatus(null);
      setThumbnailMessage(null);
    },
    onSuccess: async () => {
      // The public match card/game metadata reads the thumbnail flag from
      // `/api/matches`; invalidate both public and admin lists so any
      // open operator surfaces see the refreshed still-frame state.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: unscopedKey("public-matches") }),
        queryClient.invalidateQueries({ queryKey: unscopedKey("admin-matches") }),
      ]);
      setThumbnailStatus("success");
      setThumbnailMessage("Thumbnail regeneration started.");
      toast.success("Thumbnail regeneration started");
    },
    onError: (err) => {
      setThumbnailStatus("error");
      if (err instanceof ApiError && typeof err.detail === "string") {
        setThumbnailMessage(err.detail);
      } else {
        setThumbnailMessage("Failed to regenerate thumbnail.");
      }
      toast.error("Couldn't regenerate the thumbnail", {
        description: errorMessage(err),
      });
    },
  });

  return (
    <header
      data-testid="game-header"
      className="px-6 py-3 border-b border-rule flex items-center justify-between gap-3 max-w-7xl mx-auto"
    >
      <div className="flex items-center gap-2">
        <BackToMatchesLink
          fromTeamSlug={fromTeamSlug}
          label={`← ${backLabel}`}
          className="text-xs font-display uppercase tracking-wider text-ink-muted hover:text-ink"
        />
        {prevMatch ? (
          <Link
            to="/match/$slug"
            params={{ slug: prevMatch.slug }}
            // Preserve the `?from=` breadcrumb across prev/next so the
            // back link still points at the originating team page.
            search={fromTeamSlug ? { from: fromTeamSlug } : undefined}
            data-testid="game-prev-link"
            className="text-xs text-ink-muted hover:text-ink font-mono"
          >
            ‹ Prev
          </Link>
        ) : null}
        {nextMatch ? (
          <Link
            to="/match/$slug"
            params={{ slug: nextMatch.slug }}
            search={fromTeamSlug ? { from: fromTeamSlug } : undefined}
            data-testid="game-next-link"
            className="text-xs text-ink-muted hover:text-ink font-mono"
          >
            Next ›
          </Link>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        {isCoachOrAdmin && activeSlot ? (
          <a
            // Coach Review's route schema uses `match_id` (zod-typed
            // search-param in router.tsx); writing `?match=` here
            // would land the coach on Coach Review without the
            // selected match preloaded. Reviewer-flagged PR #21 P1.
            href={`/coach/review?match_id=${encodeURIComponent(match.id)}&slot=${encodeURIComponent(activeSlot)}`}
            data-testid="game-coach-link"
            className="text-xs text-accent hover:underline font-display uppercase tracking-wider"
          >
            Coach this match →
          </a>
        ) : null}
        {canRegenerateThumbnail ? (
          <button
            type="button"
            onClick={() => thumbnailMutation.mutate()}
            disabled={thumbnailMutation.isPending}
            data-testid="game-regenerate-thumbnail"
            className="text-[0.6rem] text-ink-muted hover:text-ink disabled:opacity-50 font-display uppercase tracking-wider rounded border border-rule px-2 py-1"
          >
            {thumbnailMutation.isPending ? "Regenerating…" : "Regen Thumb"}
          </button>
        ) : null}
        {canEdit ? (
          <Link
            to="/admin/matches"
            data-testid="game-admin-edit-link"
            className="text-[0.6rem] text-ink-muted hover:text-ink font-display uppercase tracking-wider rounded border border-rule px-2 py-1"
          >
            Edit
          </Link>
        ) : null}
        {thumbnailMessage ? (
          <span
            role={thumbnailStatus === "error" ? "alert" : "status"}
            data-testid="game-regenerate-thumbnail-status"
            data-status={thumbnailStatus ?? undefined}
            className="text-[0.65rem] text-ink-muted data-[status=success]:text-accent data-[status=error]:text-destructive"
          >
            {thumbnailMessage}
          </span>
        ) : null}
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Video — HLS playback + heartbeat + keyboard + position memory + AirPlay
// ---------------------------------------------------------------------------

function GameVideo({
  match,
  slot,
  clubLogoUrl,
}: {
  match: Match;
  slot: string;
  clubLogoUrl: string | null;
}) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [killed, setKilled] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const { hlsUrl, mp4Url } = useMemo(
    () => getStreamUrls(match, slot),
    [match, slot],
  );

  // Heartbeat — handles the 403 kill propagation by pausing the
  // video + flipping `killed` so we can surface a toast.
  useVodHeartbeat({
    videoRef,
    matchId: match.id,
    slot,
    enabled: !unavailable,
    onKilled: () => {
      setKilled(true);
      videoRef.current?.pause();
    },
  });

  // Apply the persisted playback speed on mount.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const speed = getPlaybackSpeed();
    if (speed !== 1.0) video.playbackRate = speed;
  }, [match.id, slot]);

  // Resume on loadedmetadata; save every 3 s during playback.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let saveInterval: ReturnType<typeof setInterval> | null = null;
    const onLoaded = () => {
      const resume = getResumePosition(match.id, slot, video.duration);
      if (resume != null) {
        try {
          video.currentTime = resume;
        } catch {
          /* seek can throw if metadata not ready */
        }
      }
    };
    const onPlay = () => {
      if (saveInterval) clearInterval(saveInterval);
      saveInterval = setInterval(() => {
        if (!video.paused) {
          savePlaybackPosition(
            match.id,
            slot,
            video.currentTime,
            video.duration,
          );
        }
      }, 3000);
    };
    const onPause = () => {
      if (saveInterval) {
        clearInterval(saveInterval);
        saveInterval = null;
      }
      // One final save on pause so a viewer who pauses mid-match
      // and closes the tab still gets a resume point.
      savePlaybackPosition(match.id, slot, video.currentTime, video.duration);
    };
    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    return () => {
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      if (saveInterval) clearInterval(saveInterval);
    };
  }, [match.id, slot]);

  usePublicVideoShortcuts({ videoRef, containerRef: wrapperRef });
  // M11b B10 — AirPlay button. Reuses the shared hook also used by
  // LiveShell. Hidden when no AirPlay target is available.
  const { available: airplayAvailable, casting: airplayCasting, prompt: openAirplay } =
    useAirPlay(videoRef);
  const chromecast = useChromecast(videoRef, () => ({
    url: mp4Url,
    contentType: "video/mp4",
    streamType: "BUFFERED",
    title: `${match.home_team} vs ${match.away_team}`,
    subtitle: slot === "first_half" ? "1st Half" : slot === "second_half" ? "2nd Half" : "Full Match",
    // Chromecast tile thumbnail — prefer the tenant's club mark so the
    // cast UI shows the operator's brand; fall back to the opponent's
    // per-match logo if no club logo is set; otherwise null.
    imageUrl: clubLogoUrl
      || (match.opponent_logo ? `/api/matches/${match.id}/opponent-logo` : null),
    currentTime: videoRef.current?.currentTime || 0,
  }));

  if (unavailable) {
    return (
      <div
        ref={wrapperRef}
        data-testid="game-video-unavailable"
        className="aspect-video bg-bg-card border border-rule rounded flex flex-col items-center justify-center text-ink-muted"
      >
        <p className="text-sm font-mono">Video not available</p>
        <p className="text-xs">Try again later or refresh the page.</p>
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      data-testid="game-video-wrapper"
      className="relative flex flex-col gap-2"
    >
      <HlsVideo
        ref={videoRef}
        hlsUrl={hlsUrl}
        mp4Url={mp4Url}
        controls
        className="w-full aspect-video rounded bg-black"
        onUnavailable={() => setUnavailable(true)}
        data-testid="game-video"
      />
      {killed ? (
        <div
          data-testid="game-video-killed"
          role="alert"
          className="absolute inset-x-0 bottom-12 bg-destructive text-white text-xs font-mono p-2 text-center"
        >
          This stream was disconnected by an administrator.
        </div>
      ) : null}
      {airplayAvailable || chromecast.supportedBrowser ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {airplayAvailable ? (
            <button
              type="button"
              onClick={openAirplay}
              data-testid="game-airplay-btn"
              data-casting={airplayCasting ? "true" : undefined}
              className="rounded border border-rule px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted hover:text-ink hover:bg-surface data-[casting=true]:bg-accent data-[casting=true]:text-white"
            >
              AirPlay{airplayCasting ? " · ON" : ""}
            </button>
          ) : null}
          {chromecast.supportedBrowser ? (
            <button
              type="button"
              onClick={chromecast.toggle}
              disabled={!chromecast.sdkReady}
              title={chromecast.unavailableReason || undefined}
              data-testid="game-chromecast-btn"
              data-casting={chromecast.casting ? "true" : undefined}
              className="rounded border border-rule px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted hover:text-ink hover:bg-surface data-[casting=true]:bg-accent data-[casting=true]:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cast{chromecast.casting ? ` · ${chromecast.deviceName || "ON"}` : ""}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function NoVideoPlaceholder({ match }: { match: Match }) {
  const statuses = Object.values(match.video_status || {});
  // Backend uses `transcoding` (not `processing`); the original
  // M11a port mismatched this enum value so the placeholder never
  // showed the "being processed" copy. Reviewer-flagged PR #21 P2.
  const anyProcessing = statuses.some((s) => s === "transcoding");
  return (
    <div
      data-testid="game-video-placeholder"
      className="aspect-video bg-bg-card border border-rule rounded flex flex-col items-center justify-center text-ink-muted gap-2 p-6 text-center"
    >
      {anyProcessing ? (
        <>
          <p className="text-sm font-display uppercase tracking-wider">
            Video is being processed
          </p>
          <p className="text-xs font-mono">
            Refresh in a few minutes — replay will appear once transcoding
            finishes.
          </p>
        </>
      ) : (
        <>
          <p className="text-sm font-display uppercase tracking-wider">
            Video not available
          </p>
          <p className="text-xs font-mono">
            No playable replay has been uploaded for this match yet.
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Segment buttons (two_halves)
// ---------------------------------------------------------------------------

function SegmentButtons({
  match,
  activeSlot,
  onSelect,
}: {
  match: Match;
  activeSlot: string | null;
  onSelect: (slot: string) => void;
}) {
  return (
    <nav
      data-testid="game-segments"
      role="group"
      aria-label="Match halves"
      className="flex gap-2"
    >
      {(["first_half", "second_half"] as const).map((slot) => {
        const status = match.video_status?.[slot];
        const disabled = !status || status !== "ready";
        const active = activeSlot === slot;
        const label = slot === "first_half" ? "1st Half" : "2nd Half";
        return (
          <button
            key={slot}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(slot)}
            data-testid={`game-segment-${slot}`}
            data-active={active ? "true" : undefined}
            aria-pressed={active}
            className="rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink-muted hover:text-ink hover:bg-surface data-[active=true]:bg-accent data-[active=true]:text-white data-[active=true]:border-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {label}
          </button>
        );
      })}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Sidebar — matchup, slot status, downloads
// ---------------------------------------------------------------------------

function MatchupCard({
  match,
  clubLogoUrl,
  scoreHidden,
  onRevealScore,
}: {
  match: Match;
  clubLogoUrl: string | null;
  scoreHidden: boolean;
  onRevealScore: () => void;
}) {
  const { homeUrl, awayUrl } = getSideLogos(match, clubLogoUrl);
  return (
    <article
      data-testid="game-matchup-card"
      className="rounded-lg border border-rule bg-surface p-4 flex flex-col gap-2"
    >
      <SideRow side="home" name={match.home_team} logoUrl={homeUrl} />
      <SideRow side="away" name={match.away_team} logoUrl={awayUrl} />

      <div className="border-t border-rule pt-2 flex flex-col gap-1 text-xs font-mono text-ink-muted">
        {match.date ? <span>{match.date}</span> : null}
        {match.time ? <span>{match.time}</span> : null}
        {match.location ? <span>{match.location}</span> : null}
      </div>

      {hasRecordedScore(match) ? (
        scoreHidden ? (
          <button
            type="button"
            onClick={onRevealScore}
            data-testid="game-matchup-reveal"
            className="self-start rounded border border-rule bg-bg-card hover:bg-surface px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted hover:text-ink"
          >
            Reveal score
          </button>
        ) : (
          <p
            data-testid="game-matchup-score"
            className="self-start text-lg font-mono text-ink"
          >
            {match.score_home} – {match.score_away}
          </p>
        )
      ) : null}
    </article>
  );
}

function SideRow({
  side,
  name,
  logoUrl,
}: {
  side: "home" | "away";
  name: string;
  logoUrl: string | null;
}) {
  const initials = name
    .split(/\s+/)
    .map((s) => s.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");
  return (
    <div className="flex items-center gap-2 min-w-0">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className="h-9 w-9 rounded object-contain bg-bg-card flex-shrink-0"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      ) : (
        <div
          className="h-9 w-9 rounded bg-bg-card border border-rule flex items-center justify-center text-xs font-display text-ink-muted flex-shrink-0"
          aria-hidden="true"
        >
          {initials || "?"}
        </div>
      )}
      <div className="flex flex-col min-w-0">
        <span className="text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
          {side === "home" ? "Home" : "Away"}
        </span>
        <span className="text-sm font-display text-ink truncate">
          {name}
        </span>
      </div>
    </div>
  );
}

function SlotStatusList({
  match,
  settings,
}: {
  match: Match;
  settings: AppSettings | undefined;
}) {
  const heading = settings?.game_video_status_label || "Video Status";
  const slots = Object.entries(match.video_status || {});
  if (slots.length === 0) return null;
  return (
    <section
      data-testid="game-status-list"
      className="rounded border border-rule bg-surface p-3 flex flex-col gap-1"
    >
      <h2 className="text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
        {heading}
      </h2>
      <ul className="flex flex-col gap-0.5">
        {slots.map(([slot, status]) => (
          <li
            key={slot}
            className="flex items-center justify-between text-xs font-mono"
          >
            <span className="text-ink-muted">
              {slot.replace(/_/g, " ")}
            </span>
            <span
              data-status={status}
              className="text-ink data-[status=transcoding]:text-accent data-[status=error]:text-destructive"
            >
              {status}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DownloadList({
  match,
  settings,
}: {
  match: Match;
  settings: AppSettings | undefined;
}) {
  const label = settings?.download_label || "Download";
  const readySlots = Object.entries(match.video_status || {}).filter(
    ([, status]) => status === "ready",
  );
  if (readySlots.length === 0) return null;
  return (
    <section
      data-testid="game-downloads"
      className="rounded border border-rule bg-surface p-3 flex flex-col gap-2"
    >
      <h2 className="text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </h2>
      <ul className="flex flex-col gap-1">
        {readySlots.map(([slot]) => (
          <li key={slot}>
            <a
              href={`/api/matches/${encodeURIComponent(match.id)}/download/${encodeURIComponent(slot)}`}
              data-testid={`game-download-${slot}`}
              className="text-xs text-accent hover:underline font-mono"
            >
              ↓ {slot.replace(/_/g, " ")}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

// Side-effect-free helper imports to satisfy `import { ApiError }` for
// possible future error introspection. Keeping the import bound so
// a follow-up PR (M11b live + analytics) can branch on `err instanceof ApiError`.
export const _gameShellModuleSentinel: typeof ApiError | undefined = ApiError;
